import Carousel from "@/design-system/carousel";
import type { IDiscountsPanel } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import BrandsPanel from "@/src/loyalty-card/BrandsPanel";
import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import type { ILink } from "@Components/Link";
import { scrollToY } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import * as stylex from "@stylexjs/stylex";

import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import { TransitionLink } from "../common/Transition";
import DiscountsPanel from "../discount/DiscountsPanel";
import type { IBrandsPanel } from "./BrandsPanel";
import type { ILandingPageLoyaltyCardInformation } from "./LandingPageLoyaltyCardInformation";
import { LandingPageLoyaltyCardInformation } from "./LandingPageLoyaltyCardInformation";

import * as cssPortal from "../shared/Portal.module.css";

export interface ILandingPageLoyaltyCard extends IComponentAjax<void> {
    cardInformation: ILandingPageLoyaltyCardInformation;
    brandsTable: IBrandsPanel;
    discountsPanel: IDiscountsPanel;
    specialEvents: ILoyaltyProgramSpecialEventItem[];
    contentContainers?: IContentContainer;
}

export interface ILoyaltyProgramSpecialEventItem {
    image: IImageLoader;
    link: ILink;
}

class LandingPageLoyaltyCardConnect extends ComponentAjax<
    void,
    ILandingPageLoyaltyCard & IViewportWrapperRequiredProps,
    IComponentAjaxState
> {
    public state = {
        isLoading: false,
    };

    public componentDidMount() {
        this.ajaxRequest();
    }

    public render(): JSX.Element {
        return (
            <div
                {...defaultAttributes(
                    this.props,
                    stylex.props(styles.landingPageLoyaltyCard).className,
                    cssPortal.standardContainer,
                )}
            >
                <div>
                    <LandingPageLoyaltyCardInformation
                        {...this.props.cardInformation}
                        onClick={this.scrollToOffers}
                    />
                </div>
                <div>
                    {this.carousel()}
                    <BrandsPanel {...this.props.brandsTable} />
                    {this.props.discountsPanel && (
                        <DiscountsPanel {...this.props.discountsPanel} clientId="loyaltyDiscounts" />
                    )}
                </div>
                <ContentContainer {...this.props.contentContainers} />
            </div>
        );
    }

    protected ajaxSuccess(): void {}

    protected ajaxFail(): boolean {
        return false;
    }

    private carousel = () => {
        if (!this.props.specialEvents) {
            return null;
        }

        const slider = this.props.specialEvents;

        if (slider.length === 1) {
            return <div>{this.getSpecialEvent(slider[0], 0)}</div>;
        }

        const isMobile = this.props.device === Device.Mobile;

        return (
            <div>
                <Carousel
                    centerMode={isMobile}
                    showArrows={!isMobile}
                    carouselContainerStyleX={styles.carousel}
                    itemsToShow={2}
                >
                    {this.props.specialEvents.map((item, index) => this.getSpecialEvent(item, index))}
                </Carousel>
            </div>
        );
    };

    private scrollToOffers = (): void => {
        const elem = document.getElementById("loyaltyDiscounts");
        if (!elem) {
            return;
        }

        const position = elem.getBoundingClientRect();
        scrollToY(position.top + 60);
    };

    private getSpecialEvent(item: ILoyaltyProgramSpecialEventItem, index: number): JSX.Element {
        return (
            <div key={index}>
                <TransitionLink {...item.link}>
                    <ImageLoader {...item.image} />
                </TransitionLink>
            </div>
        );
    }
}

export const LandingPageLoyaltyCard = (props: ILandingPageLoyaltyCard) => (
    <ViewportWrapper Component={LandingPageLoyaltyCardConnect} props={props} />
);

const styles = stylex.create({
    landingPageLoyaltyCard: {
        paddingBottom: 100,
    },

    carousel: {
        marginBottom: 40,
    },
});
